import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import Navbar from '../../components/navbar/navbar.jsx'
import apinode from '../../services/apinode'
import SangriaButton from './sangria.jsx'

pdfMake.addVirtualFileSystem(pdfFonts)

// ─── Formatters ───────────────────────────────────────────────────────────────
const BRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(v) || 0)

const fmtDt = (d) => {
  if (!d) return '—'
  try { return new Date(d).toLocaleString('pt-BR') } catch { return d }
}

const fmtDate = (d) => {
  if (!d) return '—'
  try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d }
}

// ─── Cores PDF ────────────────────────────────────────────────────────────────
const C = {
  primary: '#0f172a', accent: '#2563eb', accentLt: '#dbeafe',
  green: '#059669', greenLt: '#d1fae5', red: '#dc2626', redLt: '#fee2e2',
  amber: '#b45309', amberLt: '#fef3c7', purple: '#7c3aed', purpleLt: '#ede9fe',
  gray: '#64748b', border: '#e2e8f0', white: '#ffffff', lightBg: '#f8fafc',
}

const PALETTE = ['#2563eb', '#059669', '#b45309', '#7c3aed', '#be185d', '#0e7490', '#b91c1c']
const pColor = (i) => PALETTE[i % PALETTE.length]

// ─── PDF helpers ──────────────────────────────────────────────────────────────
function th(text, align = 'left', color = '#1e293b') {
  return { text, bold: true, fontSize: 7.5, color: C.white, fillColor: color, alignment: align, margin: [7, 5, 7, 5] }
}

function tc(text, opts = {}) {
  return {
    text: String(text ?? '—'), fontSize: 8, alignment: opts.align || 'left',
    color: opts.color || C.primary, fillColor: opts.bg || C.white,
    bold: opts.bold || false, italics: opts.italics || false,
    margin: [7, 4, 7, 4],
  }
}

function sBar(text, color = C.accent) {
  return {
    columns: [
      { canvas: [{ type: 'rect', x: 0, y: 0, w: 3, h: 18, r: 2, color }], width: 10 },
      { text: text.toUpperCase(), fontSize: 8, bold: true, color, letterSpacing: 0.8, margin: [4, 2, 0, 0] },
    ],
    margin: [0, 0, 0, 6],
  }
}

function confLine(label, value, bgColor = C.white, valColor = C.primary, bold = false) {
  return [
    { text: label, fontSize: 8, color: bold ? C.primary : C.gray, fillColor: bgColor, margin: [10, 5, 10, 5], bold },
    { text: value, fontSize: bold ? 9 : 8, bold: true, color: valColor, fillColor: bgColor, alignment: 'right', margin: [10, 5, 10, 5] },
  ]
}

// ─── PDF GERADOR ──────────────────────────────────────────────────────────────
// Calcula o total já recebido de um pedido considerando todos os caixas
// rec = max(recebido neste caixa, liquido - saldo pendente no CR)
const getRecPedido = (p) => {
  const liq = p.valorBruto - p.abatimento
  const recNesteCaixa = p.pagamentos.reduce((t, pg) => t + pg.vlrRecebido, 0)
  return Math.max(recNesteCaixa, liq - p.saldoAbertoCR)
}

function gerarPDF(id_cx, salesData, dataCX) {
  if (!salesData?.length) return

  // Agrupa por pedido
  // IMPORTANTE:
  // - valorBruto    = valor real do pedido (pedido_int.valor)
  // - totalParcCR   = soma de TODAS as parcelas no CONTAS_A_RECEBER
  // - saldoAbertoCR = parcelas ainda Em Aberto (não recebidas em nenhum caixa)
  // - jaRecebidoCR  = totalParcCR - saldoAbertoCR (pago em caixas anteriores + atual)
  // - recebidoNesteCaixa = soma dos vlr_recebido deste caixa para este pedido
  // Debug — remove depois
  if (salesData?.length > 0) {
  }

  const pedidoMap = {}
  salesData.forEach(s => {
    if (!pedidoMap[s.idpedido]) {
      pedidoMap[s.idpedido] = {
        idpedido:     s.idpedido,
        cliente:      s.cliente,
        abatimento:   parseFloat(s.valor_devolvido || 0),
        pagamentos:   [],
        valorBruto:   parseFloat(s.valorpedido) || 0,
        // saldo pendente real vem do CONTAS_A_RECEBER
        saldoAbertoCR: parseFloat(s.saldo_aberto_cr || 0),
      }
    }
    pedidoMap[s.idpedido].pagamentos.push({
      forma:       s.forma_pgto,
      vlrRecebido: parseFloat(s.vlr_recebido) || 0,
      troco:       parseFloat(s.troco) || 0,
      data:        s.data,
    })
  })
  const grouped = Object.values(pedidoMap)

  const fundo        = parseFloat(salesData[0]?.fundo_caixa) || 0
  const operador     = salesData[0]?.nome_abertura || 'N/A'
  const dateFmt      = dataCX ? new Date(dataCX).toLocaleDateString('pt-BR') : '—'
  const horFmt       = dataCX ? new Date(dataCX).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—'

  // Totais
  const totalBruto    = grouped.reduce((t, p) => t + p.valorBruto, 0)
  const totalAbat     = grouped.reduce((t, p) => t + p.abatimento, 0)
  const totalLiquido  = totalBruto - totalAbat
  // Recebido NESTE caixa (apenas os registros do registro_caixa atual)
  const totalRecebido = salesData.reduce((t, s) => t + (parseFloat(s.vlr_recebido) || 0), 0)
  const totalTroco    = salesData.reduce((t, s) => t + (parseFloat(s.troco) || 0), 0)
  const totalNoCaixa  = fundo + totalRecebido - totalTroco

  // Saldo pendente REAL = parcelas ainda Em Aberto no CONTAS_A_RECEBER
  // Considera pagamentos feitos em QUALQUER caixa anterior
  const saldoPendente = grouped.reduce((t, p) => t + p.saldoAbertoCR, 0)

  // Pedidos parciais = ainda têm parcelas Em Aberto
  const pedidosParciais = grouped.filter(p => p.saldoAbertoCR > 0.009)

  // Por forma
  const pgtoMap = {}
  salesData.forEach(s => {
    const k = s.forma_pgto || 'Não informado'
    pgtoMap[k] = (pgtoMap[k] || 0) + (parseFloat(s.vlr_recebido) || 0)
  })
  const totalPgtos = Object.values(pgtoMap).reduce((a, b) => a + b, 0)

  // Linhas da tabela de pedidos
  const pedidoRows = grouped.flatMap((p, si) => {
    const bg         = si % 2 === 0 ? C.lightBg : C.white
    const recNesteCaixa = p.pagamentos.reduce((t, pg) => t + pg.vlrRecebido, 0)
    const liquido       = p.valorBruto - p.abatimento
    const saldo         = p.saldoAbertoCR
    // recebido = liquido - saldo pendente (considera TODOS os caixas anteriores)
    const recPedido     = Math.max(recNesteCaixa, liquido - saldo)
    const isParcial     = saldo > 0.009
    const isQuitado     = !isParcial

    return p.pagamentos.map((pg, fi) => {
      const isFirst = fi === 0
      return [
        isFirst
          ? { text: String(p.idpedido), fontSize: 8, bold: true, color: isQuitado ? C.green : C.amber, fillColor: bg, margin: [7, 4, 7, 4] }
          : tc('', { bg }),
        isFirst
          ? { text: p.cliente || '—', fontSize: 8, color: C.primary, fillColor: bg, margin: [7, 4, 7, 4] }
          : tc('↳', { bg, italics: true, color: C.gray }),
        tc(pg.forma || '—', { bg }),
        isFirst ? tc(BRL(p.valorBruto), { bg, align: 'right', color: C.gray }) : tc('', { bg }),
        isFirst && p.abatimento > 0.009
          ? { text: `−${BRL(p.abatimento)}`, fontSize: 8, bold: true, color: C.red, fillColor: '#fff5f5', alignment: 'right', margin: [7, 4, 7, 4] }
          : tc('', { bg }),
        isFirst
          ? { text: BRL(liquido), fontSize: 8, bold: true, color: C.accent, fillColor: bg, alignment: 'right', margin: [7, 4, 7, 4] }
          : tc('', { bg }),
        { text: BRL(pg.vlrRecebido), fontSize: 8, bold: true, color: C.green, fillColor: bg, alignment: 'right', margin: [7, 4, 7, 4] },
        isFirst
          ? {
              text: isParcial ? `⚠ ${BRL(saldo)}` : '✓ Quitado',
              fontSize: 7.5, bold: true,
              color: isParcial ? C.amber : C.green,
              fillColor: isParcial ? C.amberLt : C.greenLt,
              alignment: 'center', margin: [7, 4, 7, 4],
            }
          : tc('', { bg }),
      ]
    })
  })

  // Linhas dos parciais
  const parciaisRows = pedidosParciais.map((p, i) => {
    const recPedido = p.pagamentos.reduce((t, pg) => t + pg.vlrRecebido, 0)
    const liquido   = p.valorBruto - p.abatimento
    const saldo     = liquido - recPedido
    const bg        = i % 2 === 0 ? C.amberLt : '#fffbeb'
    return [
      tc(String(p.idpedido), { bg, bold: true, color: C.amber }),
      tc(p.cliente || '—', { bg }),
      tc(BRL(liquido), { bg, align: 'right', color: C.primary }),
      tc(BRL(recPedido), { bg, align: 'right', color: C.green }),
      { text: BRL(saldo), fontSize: 8, bold: true, color: C.red, fillColor: bg, alignment: 'right', margin: [7, 4, 7, 4] },
      tc(`${Math.round((recPedido / liquido) * 100)}%`, { bg, align: 'center', color: C.amber, bold: true }),
    ]
  })

  const doc = {
    pageSize: 'A4',
    pageMargins: [32, 50, 32, 42],

    header: () => ({
      columns: [
        { text: `RELATÓRIO DE CAIXA Nº ${id_cx}  ·  ${dateFmt}`, fontSize: 6.5, bold: true, color: C.gray },
        { text: `Operador: ${operador}  ·  ${horFmt}`, fontSize: 6.5, color: C.gray, alignment: 'right' },
      ],
      margin: [32, 16, 32, 0],
    }),

    footer: (pg, total) => ({
      columns: [
        { text: `Gerado em ${new Date().toLocaleString('pt-BR')} — Sistema ZipHub PDV`, fontSize: 6.5, color: '#94a3b8' },
        { text: `Página ${pg} / ${total}`, fontSize: 6.5, color: '#94a3b8', alignment: 'right' },
      ],
      margin: [32, 0, 32, 16],
    }),

    content: [
      // ── CABEÇALHO ─────────────────────────────────────────────────────────
      {
        canvas: [{ type: 'rect', x: 0, y: 0, w: 531, h: 70, r: 8, color: C.primary }],
        absolutePosition: { x: 32, y: 50 },
      },
      {
        columns: [
          {
            stack: [
              { text: 'RELATÓRIO DE CAIXA', fontSize: 18, bold: true, color: C.white, letterSpacing: -0.3 },
              { text: `Caixa Nº ${id_cx}  ·  Abertura ${dateFmt} às ${horFmt}`, fontSize: 8, color: 'rgba(255,255,255,0.6)', margin: [0, 4, 0, 0] },
              {
                columns: [
                  saldoPendente > 0.009
                    ? { text: `⚠  ${pedidosParciais.length} pedido(s) com pagamento parcial`, fontSize: 8, bold: true, color: '#fbbf24', margin: [0, 6, 0, 0] }
                    : { text: '✓  Todos os pedidos foram quitados', fontSize: 8, bold: true, color: '#86efac', margin: [0, 6, 0, 0] },
                ],
              },
            ],
          },
          {
            stack: [
              { text: 'OPERADOR', fontSize: 6, bold: true, color: 'rgba(255,255,255,0.45)', letterSpacing: 1 },
              { text: operador, fontSize: 12, bold: true, color: C.white, margin: [0, 3, 0, 0] },
            ],
            alignment: 'right',
          },
        ],
        margin: [8, 0, 8, 0],
      },
      { text: '\n' },

      // ── KPIs LINHA 1 ──────────────────────────────────────────────────────
      {
        columns: [
          {
            stack: [
              { canvas: [{ type: 'rect', x: 0, y: 0, w: 120, h: 55, r: 7, color: '#1e3a8a' }] },
              { stack: [{ text: 'FUNDO', fontSize: 6, bold: true, color: '#93c5fd', letterSpacing: 0.8 }, { text: BRL(fundo), fontSize: 11, bold: true, color: C.white }], margin: [9, -47, 9, 0] },
            ],
          },
          {
            stack: [
              { canvas: [{ type: 'rect', x: 0, y: 0, w: 120, h: 55, r: 7, color: '#1e40af' }] },
              { stack: [{ text: 'BRUTO', fontSize: 6, bold: true, color: '#93c5fd', letterSpacing: 0.8 }, { text: BRL(totalBruto), fontSize: 11, bold: true, color: C.white }], margin: [9, -47, 9, 0] },
            ],
          },
          {
            stack: [
              { canvas: [{ type: 'rect', x: 0, y: 0, w: 120, h: 55, r: 7, color: '#92400e' }] },
              { stack: [{ text: 'ABATIMENTOS', fontSize: 6, bold: true, color: '#fde68a', letterSpacing: 0.8 }, { text: BRL(totalAbat), fontSize: 11, bold: true, color: C.white }], margin: [9, -47, 9, 0] },
            ],
          },
          {
            stack: [
              { canvas: [{ type: 'rect', x: 0, y: 0, w: 120, h: 55, r: 7, color: '#065f46' }] },
              { stack: [{ text: 'LÍQUIDO', fontSize: 6, bold: true, color: '#6ee7b7', letterSpacing: 0.8 }, { text: BRL(totalLiquido), fontSize: 11, bold: true, color: C.white }], margin: [9, -47, 9, 0] },
            ],
          },
        ],
        columnGap: 8,
        margin: [0, 14, 0, 8],
      },
      // ── KPIs LINHA 2 ──────────────────────────────────────────────────────
      {
        columns: [
          {
            stack: [
              { canvas: [{ type: 'rect', x: 0, y: 0, w: 120, h: 55, r: 7, color: '#14532d' }] },
              { stack: [{ text: 'RECEBIDO', fontSize: 6, bold: true, color: '#86efac', letterSpacing: 0.8 }, { text: BRL(totalRecebido), fontSize: 11, bold: true, color: C.white }], margin: [9, -47, 9, 0] },
            ],
          },
          {
            stack: [
              { canvas: [{ type: 'rect', x: 0, y: 0, w: 120, h: 55, r: 7, color: '#7f1d1d' }] },
              { stack: [{ text: 'TROCO', fontSize: 6, bold: true, color: '#fca5a5', letterSpacing: 0.8 }, { text: BRL(totalTroco), fontSize: 11, bold: true, color: C.white }], margin: [9, -47, 9, 0] },
            ],
          },
          {
            stack: [
              { canvas: [{ type: 'rect', x: 0, y: 0, w: 120, h: 55, r: 7, color: saldoPendente > 0.009 ? '#78350f' : '#14532d' }] },
              {
                stack: [
                  { text: 'SALDO PEND.', fontSize: 6, bold: true, color: saldoPendente > 0.009 ? '#fde68a' : '#86efac', letterSpacing: 0.8 },
                  { text: BRL(saldoPendente), fontSize: 11, bold: true, color: C.white },
                ],
                margin: [9, -47, 9, 0],
              },
            ],
          },
          {
            stack: [
              { canvas: [{ type: 'rect', x: 0, y: 0, w: 120, h: 55, r: 7, color: '#1e293b' }] },
              { stack: [{ text: 'CAIXA FÍSICO', fontSize: 6, bold: true, color: '#fbbf24', letterSpacing: 0.8 }, { text: BRL(totalNoCaixa), fontSize: 11, bold: true, color: '#fbbf24' }], margin: [9, -47, 9, 0] },
            ],
          },
        ],
        columnGap: 8,
        margin: [0, 0, 0, 20],
      },

      // ── ALERTA PARCIAIS ───────────────────────────────────────────────────
      ...(pedidosParciais.length > 0 ? [{
        columns: [
          { canvas: [{ type: 'rect', x: 0, y: 0, w: 531, h: 36, r: 7, color: C.amberLt }] },
          {
            columns: [
              { text: `⚠  ATENÇÃO: ${pedidosParciais.length} pedido(s) com pagamento PARCIAL — Saldo total pendente: ${BRL(saldoPendente)}`, fontSize: 8.5, bold: true, color: C.amber, margin: [12, 10, 0, 0] },
            ],
            absolutePosition: { x: 32, y: 0 },
          },
        ],
        margin: [0, 0, 0, 18],
      }] : []),

      // ── TABELA DE PEDIDOS ─────────────────────────────────────────────────
      sBar('Detalhamento por Pedido', C.accent),
      {
        table: {
          headerRows: 1,
          widths: [40, '*', 64, 56, 56, 58, 58, 58],
          body: [
            [
              th('Pedido'), th('Cliente'), th('Forma'),
              th('Bruto', 'right'), th('Abat.', 'right'),
              th('Líquido', 'right'), th('Recebido', 'right'),
              th('Status', 'center'),
            ],
            ...pedidoRows,
            [
              { text: 'TOTAIS', colSpan: 3, bold: true, fontSize: 8, color: C.white, fillColor: C.primary, alignment: 'center', margin: [7, 6, 7, 6] },
              {}, {},
              tc(BRL(totalBruto),    { bg: C.primary, align: 'right', color: '#93c5fd', bold: true }),
              tc(`−${BRL(totalAbat)}`, { bg: C.primary, align: 'right', color: '#fca5a5', bold: true }),
              tc(BRL(totalLiquido),  { bg: C.primary, align: 'right', color: '#c4b5fd', bold: true }),
              tc(BRL(totalRecebido), { bg: C.primary, align: 'right', color: '#6ee7b7', bold: true }),
              tc(saldoPendente > 0.009 ? `⚠ ${BRL(saldoPendente)}` : '✓ Quitado',
                { bg: C.primary, align: 'center', color: saldoPendente > 0.009 ? '#fbbf24' : '#86efac', bold: true }),
            ],
          ],
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1.5 : 0.3,
          vLineWidth: () => 0,
          hLineColor: (i) => i === 1 ? C.accent : C.border,
          paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 20],
      },

      // ── PEDIDOS PARCIAIS ──────────────────────────────────────────────────
      ...(pedidosParciais.length > 0 ? [
        sBar('Pedidos com Pagamento Parcial — Conferência de Saldos', C.amber),
        {
          table: {
            headerRows: 1,
            widths: [50, '*', 70, 70, 70, 50],
            body: [
              [
                th('Pedido', 'left', '#92400e'),
                th('Cliente', 'left', '#92400e'),
                th('Líquido', 'right', '#92400e'),
                th('Recebido', 'right', '#92400e'),
                th('Saldo', 'right', '#92400e'),
                th('% Pago', 'center', '#92400e'),
              ],
              ...parciaisRows,
              [
                { text: 'TOTAL PENDENTE', colSpan: 4, bold: true, fontSize: 8, color: C.white, fillColor: C.amber, alignment: 'center', margin: [7, 6, 7, 6] },
                {}, {}, {},
                { text: BRL(saldoPendente), fontSize: 9, bold: true, color: C.white, fillColor: C.amber, alignment: 'right', margin: [7, 6, 7, 6] },
                tc('', { bg: C.amber }),
              ],
            ],
          },
          layout: {
            hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1.5 : 0.3,
            vLineWidth: () => 0,
            hLineColor: (i) => i === 1 ? C.amber : '#fde68a',
            paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
          },
          margin: [0, 0, 0, 20],
        },
      ] : []),

      // ── FORMAS + CONFERÊNCIA ──────────────────────────────────────────────
      {
        columns: [
          {
            width: '50%',
            stack: [
              sBar('Resumo por Forma de Pagamento', C.green),
              {
                table: {
                  headerRows: 1,
                  widths: ['*', 80],
                  body: [
                    [th('Forma', 'left', '#065f46'), th('Recebido', 'right', '#065f46')],
                    ...Object.entries(pgtoMap).map(([forma, val], i) => {
                      const bg = i % 2 === 0 ? C.lightBg : C.white
                      const pct = totalPgtos > 0 ? Math.round((val / totalPgtos) * 100) : 0
                      const barW = Math.max(4, Math.round((val / totalPgtos) * 140))
                      return [
                        {
                          stack: [
                            { text: forma, fontSize: 8, bold: true, color: C.primary },
                            {
                              canvas: [
                                { type: 'rect', x: 0, y: 0, w: 140, h: 3, r: 2, color: '#e2e8f0' },
                                { type: 'rect', x: 0, y: 0, w: barW, h: 3, r: 2, color: pColor(i) },
                              ],
                              margin: [0, 3, 0, 0],
                            },
                            { text: `${pct}% do total`, fontSize: 6.5, color: C.gray, margin: [0, 2, 0, 0] },
                          ],
                          fillColor: bg, margin: [8, 5, 8, 5],
                        },
                        { text: BRL(val), fontSize: 8, bold: true, color: pColor(i), fillColor: bg, alignment: 'right', margin: [7, 5, 8, 5] },
                      ]
                    }),
                    [
                      { text: 'TOTAL RECEBIDO', bold: true, fontSize: 9, color: C.white, fillColor: C.green, margin: [8, 7, 8, 7] },
                      { text: BRL(totalPgtos), bold: true, fontSize: 9, color: C.white, fillColor: C.green, alignment: 'right', margin: [7, 7, 8, 7] },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length) ? 1.5 : 0.3,
                  vLineWidth: () => 0,
                  hLineColor: (i) => i === 1 ? C.green : C.border,
                  paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
                },
              },
            ],
          },
          { width: 14, text: '' },
          {
            width: '*',
            stack: [
              sBar('Conferência de Fechamento', saldoPendente > 0.009 ? C.amber : C.green),
              {
                table: {
                  widths: ['*', 80],
                  body: [
                    confLine('Fundo de Caixa (abertura)', BRL(fundo), C.lightBg),
                    confLine('(+) Vendas Brutas', BRL(totalBruto)),
                    ...(totalAbat > 0.009 ? [
                      [
                        { text: '(−) Abatimentos/Descontos', fontSize: 8, color: C.red, fillColor: '#fff5f5', margin: [10, 5, 10, 5] },
                        { text: `−${BRL(totalAbat)}`, fontSize: 8, bold: true, color: C.red, fillColor: '#fff5f5', alignment: 'right', margin: [10, 5, 10, 5] },
                      ],
                    ] : [confLine('(−) Abatimentos', BRL(0), C.lightBg, C.gray)]),
                    [
                      { text: '= Vendas Líquidas', fontSize: 8, bold: true, color: C.accent, fillColor: C.accentLt, margin: [10, 6, 10, 6] },
                      { text: BRL(totalLiquido), fontSize: 8, bold: true, color: C.accent, fillColor: C.accentLt, alignment: 'right', margin: [10, 6, 10, 6] },
                    ],
                    confLine('(+) Total Recebido', BRL(totalRecebido), C.white, C.green),
                    ...(totalTroco > 0.009 ? [[
                      { text: '(−) Troco Entregue', fontSize: 8, color: C.red, fillColor: '#fff5f5', margin: [10, 5, 10, 5] },
                      { text: `−${BRL(totalTroco)}`, fontSize: 8, bold: true, color: C.red, fillColor: '#fff5f5', alignment: 'right', margin: [10, 5, 10, 5] },
                    ]] : []),
                    ...(saldoPendente > 0.009 ? [[
                      {
                        stack: [
                          { text: '⚠  SALDO PENDENTE', fontSize: 8, bold: true, color: C.amber },
                          { text: `${pedidosParciais.length} pedido(s) parcial(is)`, fontSize: 6.5, color: C.amber, italics: true, margin: [0, 2, 0, 0] },
                        ],
                        fillColor: C.amberLt, margin: [10, 6, 10, 6],
                      },
                      { text: BRL(saldoPendente), fontSize: 9, bold: true, color: C.amber, fillColor: C.amberLt, alignment: 'right', margin: [10, 6, 10, 6] },
                    ]] : [[
                      { text: '✓  Sem saldo pendente', fontSize: 8, color: C.green, fillColor: C.greenLt, margin: [10, 5, 10, 5] },
                      { text: BRL(0), fontSize: 8, bold: true, color: C.green, fillColor: C.greenLt, alignment: 'right', margin: [10, 5, 10, 5] },
                    ]]),
                    [
                      {
                        stack: [
                          { text: 'TOTAL FÍSICO NO CAIXA', fontSize: 9, bold: true, color: C.white },
                          { text: 'Fundo + Recebido − Troco', fontSize: 6.5, color: 'rgba(255,255,255,.5)', margin: [0, 2, 0, 0] },
                        ],
                        fillColor: C.primary, margin: [10, 9, 10, 9],
                      },
                      { text: BRL(totalNoCaixa), fontSize: 13, bold: true, color: '#fbbf24', fillColor: C.primary, alignment: 'right', margin: [10, 9, 10, 9] },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1.5 : 0.3,
                  vLineWidth: () => 0,
                  hLineColor: () => C.border,
                  paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
                },
              },
            ],
          },
        ],
        margin: [0, 0, 0, 28],
      },

      // ── ASSINATURAS ───────────────────────────────────────────────────────
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 531, y2: 0, lineWidth: 0.7, dash: { length: 5, space: 4 }, lineColor: '#cbd5e1' }],
        margin: [0, 0, 0, 24],
      },
      {
        columns: [
          {
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 215, y2: 0, lineWidth: 1, lineColor: '#94a3b8' }] },
              { text: 'Responsável pelo Fechamento', fontSize: 7.5, color: '#94a3b8', alignment: 'center', margin: [0, 5, 0, 0] },
            ],
            alignment: 'center',
          },
          { width: 60, text: '' },
          {
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 215, y2: 0, lineWidth: 1, lineColor: '#94a3b8' }] },
              { text: 'Conferente / Supervisor', fontSize: 7.5, color: '#94a3b8', alignment: 'center', margin: [0, 5, 0, 0] },
            ],
            alignment: 'center',
          },
        ],
      },
    ],

    defaultStyle: { font: 'Roboto', fontSize: 8.5, color: C.primary },
  }

  pdfMake.createPdf(doc).open()
  localStorage.setItem('lastGeneratedPDF', JSON.stringify(doc))
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;600;700&display=swap');

:root {
  --ink: #0f172a; --ink2: #334155; --muted: #64748b; --faint: #94a3b8;
  --bg: #f0f4ff; --surface: #ffffff; --surface2: #f8fafc; --border: #e2e8f0;
  --blue: #2563eb; --blue-dk: #1d4ed8; --blue-lt: #dbeafe; --blue-xt: #eff6ff;
  --green: #059669; --green-lt: #d1fae5; --green-xt: #f0fdf4;
  --red: #dc2626; --red-lt: #fee2e2; --red-xt: #fff5f5;
  --amber: #d97706; --amber-lt: #fef3c7; --amber-xt: #fffbeb;
  --purple: #7c3aed; --purple-lt: #ede9fe;
  --r: 14px; --rs: 9px; --f: 'Figtree', sans-serif; --fm: 'JetBrains Mono', monospace;
  --sh: 0 1px 3px rgba(0,0,0,.06), 0 4px 16px rgba(37,99,235,.06);
  --sh-lg: 0 4px 24px rgba(0,0,0,.08), 0 12px 48px rgba(37,99,235,.10);
  --tr: .18s cubic-bezier(.4,0,.2,1);
}

*, *::before, *::after { box-sizing: border-box; }

.g-root { font-family: var(--f); background: var(--bg); min-height: 100vh; padding: 28px 20px 72px; color: var(--ink); }
.g-wrap { max-width: 1060px; margin: 0 auto; }

/* ── hero ── */
.g-hero { position: relative; overflow: hidden; background: var(--ink); border-radius: var(--r); padding: 30px 36px; margin-bottom: 28px; display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; }
.g-hero::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse at 70% 50%, rgba(37,99,235,.35) 0%, transparent 65%); pointer-events: none; }
.g-hero::after { content: ''; position: absolute; right: -40px; top: -40px; width: 220px; height: 220px; border-radius: 50%; background: rgba(99,102,241,.12); pointer-events: none; }
.g-hero-text h1 { font-size: 1.75rem; font-weight: 900; color: #fff; margin: 0 0 4px; letter-spacing: -.04em; }
.g-hero-text p { color: rgba(255,255,255,.55); font-size: .88rem; margin: 0; font-weight: 500; }
.g-hero-btns { display: flex; gap: 10px; flex-wrap: wrap; position: relative; z-index: 1; }

/* ── stat cards ── */
.g-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 24px; }
@media(max-width:840px){ .g-stats { grid-template-columns: 1fr 1fr; } }
.g-stat { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r); padding: 20px 22px; box-shadow: var(--sh); transition: transform var(--tr), box-shadow var(--tr); position: relative; overflow: hidden; }
.g-stat:hover { transform: translateY(-2px); box-shadow: var(--sh-lg); }
.g-stat-accent { position: absolute; top: 0; left: 0; right: 0; height: 3px; border-radius: var(--r) var(--r) 0 0; }
.g-stat-ico { width: 42px; height: 42px; border-radius: 11px; display: flex; align-items: center; justify-content: center; font-size: 1.15rem; margin-bottom: 14px; }
.g-stat label { display: block; font-size: .68rem; font-weight: 800; text-transform: uppercase; letter-spacing: .09em; color: var(--muted); margin-bottom: 5px; }
.g-stat .g-val { font-size: 1.45rem; font-weight: 900; letter-spacing: -.03em; line-height: 1; }
.g-stat .g-sub { font-size: .75rem; color: var(--muted); margin-top: 3px; font-weight: 500; }

/* ── grid ── */
.g-grid { display: grid; grid-template-columns: 400px 1fr; gap: 20px; align-items: start; margin-bottom: 24px; }
@media(max-width:860px){ .g-grid { grid-template-columns: 1fr; } }

/* ── card ── */
.g-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r); box-shadow: var(--sh); overflow: hidden; }
.g-card-hd { padding: 18px 22px 15px; border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 12px; }
.g-card-hd-ico { width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; }
.g-card-hd-title { font-size: .95rem; font-weight: 800; color: var(--ink); margin: 0; letter-spacing: -.01em; }
.g-card-hd-sub { font-size: .75rem; color: var(--muted); margin: 2px 0 0; font-weight: 500; }
.g-card-hd-right { margin-left: auto; }
.g-card-bd { padding: 22px; }

/* ── form ── */
.g-field { margin-bottom: 16px; }
.g-field:last-child { margin-bottom: 0; }
.g-label { display: block; font-size: .68rem; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; color: var(--muted); margin-bottom: .4rem; }
.g-input { width: 100%; background: var(--surface2); border: 1.5px solid var(--border); border-radius: var(--rs); padding: .65rem 1rem; font-family: var(--f); font-size: .9rem; font-weight: 500; color: var(--ink); outline: none; transition: border-color var(--tr), box-shadow var(--tr), background var(--tr); }
.g-input:focus { border-color: var(--blue); background: var(--surface); box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
.g-input:disabled { background: var(--blue-lt); color: var(--blue-dk); font-weight: 700; border-color: transparent; cursor: not-allowed; }
.g-input::placeholder { color: var(--faint); }

/* ── buttons ── */
.g-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; padding: .68rem 1.35rem; border: none; border-radius: var(--rs); font-family: var(--f); font-size: .85rem; font-weight: 700; cursor: pointer; transition: all var(--tr); white-space: nowrap; letter-spacing: -.01em; }
.g-btn:disabled { opacity: .45; cursor: not-allowed; transform: none !important; box-shadow: none !important; }
.g-btn-full { width: 100%; }
.g-btn-primary { background: var(--blue); color: #fff; box-shadow: 0 3px 12px rgba(37,99,235,.25); }
.g-btn-primary:hover:not(:disabled) { background: var(--blue-dk); transform: translateY(-1px); box-shadow: 0 6px 20px rgba(37,99,235,.3); }
.g-btn-ghost { background: transparent; border: 1.5px solid rgba(255,255,255,.25); color: rgba(255,255,255,.9); }
.g-btn-ghost:hover:not(:disabled) { background: rgba(255,255,255,.12); border-color: rgba(255,255,255,.5); }
.g-btn-outline { background: var(--surface); border: 1.5px solid var(--border); color: var(--ink2); }
.g-btn-outline:hover:not(:disabled) { border-color: var(--blue); color: var(--blue); background: var(--blue-xt); }
.g-btn-danger { background: var(--red); color: #fff; box-shadow: 0 3px 12px rgba(220,38,38,.2); }
.g-btn-danger:hover:not(:disabled) { background: #b91c1c; transform: translateY(-1px); }
.g-btn-sm { padding: .48rem .95rem; font-size: .78rem; }
.g-btn-xs { padding: .32rem .7rem; font-size: .72rem; }
.g-icon-btn { width: 34px; height: 34px; padding: 0; border-radius: 8px; background: var(--surface2); border: 1.5px solid var(--border); color: var(--muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 14px; transition: all var(--tr); flex-shrink: 0; }
.g-icon-btn:hover { background: var(--blue-lt); border-color: var(--blue); color: var(--blue); }
.g-icon-btn.danger:hover { background: var(--red-lt); border-color: var(--red); color: var(--red); }
.g-icon-btn.success:hover { background: var(--green-lt); border-color: var(--green); color: var(--green); }
.g-icon-btn:disabled { opacity: .35; cursor: not-allowed; pointer-events: none; }

/* ── table ── */
.g-table-wrap { overflow-x: auto; }
.g-table { width: 100%; border-collapse: collapse; font-size: .82rem; }
.g-table th { background: var(--surface2); padding: 11px 14px; text-align: left; font-size: .67rem; font-weight: 800; text-transform: uppercase; letter-spacing: .09em; color: var(--muted); border-bottom: 2px solid var(--border); white-space: nowrap; cursor: pointer; user-select: none; transition: background var(--tr); }
.g-table th:hover { background: var(--border); color: var(--ink); }
.g-table td { padding: 13px 14px; border-bottom: 1px solid var(--border); vertical-align: middle; color: var(--ink2); }
.g-table tr:last-child td { border-bottom: none; }
.g-table tr:hover td { background: var(--blue-xt); }
.g-table tr.row-open td { background: var(--green-xt); }
.g-table tr.row-partial td { background: var(--amber-xt); }
.g-table tr.row-closed td { opacity: .6; }

/* ── badge ── */
.g-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 99px; font-size: .68rem; font-weight: 800; letter-spacing: .04em; white-space: nowrap; }
.g-badge-green  { background: var(--green-lt);  color: var(--green); }
.g-badge-amber  { background: var(--amber-lt);  color: var(--amber); }
.g-badge-red    { background: var(--red-lt);    color: var(--red); }
.g-badge-blue   { background: var(--blue-lt);   color: var(--blue-dk); }
.g-badge-gray   { background: var(--surface2);  color: var(--muted); border: 1px solid var(--border); }
.g-badge-purple { background: var(--purple-lt); color: var(--purple); }

/* ── modal ── */
.g-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.5); backdrop-filter: blur(6px); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn .14s ease; }
.g-modal { background: var(--surface); border-radius: var(--r); box-shadow: var(--sh-lg); width: 100%; max-width: 1000px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; animation: slideUp .2s ease; }
.g-modal-sm { max-width: 460px; }
.g-modal-hd { padding: 20px 26px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
.g-modal-hd h4 { font-size: 1rem; font-weight: 800; margin: 0; letter-spacing: -.02em; }
.g-modal-bd { padding: 24px 26px; overflow-y: auto; flex: 1; }
.g-modal-ft { padding: 16px 26px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 10px; }
.g-close { background: none; border: none; font-size: 1.2rem; color: var(--muted); cursor: pointer; padding: 4px 8px; border-radius: 6px; transition: all var(--tr); line-height: 1; }
.g-close:hover { background: var(--surface2); color: var(--ink); }

/* ── conferência modal ── */
.conf-row { display: flex; align-items: center; justify-content: space-between; padding: 11px 14px; border-radius: 9px; margin-bottom: 4px; }
.conf-row:last-child { margin-bottom: 0; }
.conf-label { font-size: .85rem; color: var(--ink2); font-weight: 500; }
.conf-value { font-size: .9rem; font-weight: 800; font-family: var(--fm); }

/* ── code display ── */
.g-code { font-family: var(--fm); font-size: 1.5rem; font-weight: 700; letter-spacing: .2em; color: var(--blue); background: var(--blue-xt); border: 2px dashed var(--blue); border-radius: 10px; padding: 16px 24px; text-align: center; margin: 14px 0; }

/* ── info card ── */
.g-info { border-radius: 11px; padding: 16px 18px; display: flex; gap: 13px; align-items: flex-start; }
.g-info-ico { font-size: 1.3rem; flex-shrink: 0; }
.g-info-title { font-size: .85rem; font-weight: 700; margin-bottom: 3px; }
.g-info-text { font-size: .8rem; line-height: 1.55; color: var(--ink2); }

/* ── spinner ── */
.g-spin { display: inline-block; width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .6s linear infinite; }
.g-spin-dark { border-color: rgba(37,99,235,.2); border-top-color: var(--blue); }

/* ── partial bar ── */
.partial-bar-wrap { background: var(--border); border-radius: 99px; height: 6px; flex: 1; overflow: hidden; }
.partial-bar { height: 100%; border-radius: 99px; transition: width .4s ease; }

/* ── misc ── */
.g-mono { font-family: var(--fm); }
.g-divider { border: none; border-top: 1px solid var(--border); margin: 20px 0; }
.g-empty { text-align: center; padding: 48px 24px; color: var(--muted); }
.g-empty-ico { font-size: 2.5rem; display: block; margin-bottom: 12px; opacity: .4; }
[data-tip] { position: relative; }
[data-tip]:hover::after { content: attr(data-tip); position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%); background: var(--ink); color: #fff; font-size: .68rem; font-weight: 600; padding: 4px 10px; border-radius: 6px; white-space: nowrap; pointer-events: none; z-index: 200; font-family: var(--f); }

@keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
@keyframes slideUp { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: translateY(0) } }
@keyframes spin    { to { transform: rotate(360deg) } }

@media(max-width:600px) {
  .g-hero { flex-direction: column; }
  .g-hero-btns { width: 100%; }
  .g-hero-btns .g-btn { flex: 1; }
}
`

// ─── component ────────────────────────────────────────────────────────────────
function AberturaCaixa() {
  const [formData,      setFormData]      = useState({ troco: '', data_abertura: '', usuario_abertura: localStorage.idusuario || '' })
  const [caixas,        setCaixas]        = useState([])
  const [showList,      setShowList]      = useState(false)
  const [showConf,      setShowConf]      = useState(false)
  const [showConfer,    setShowConfer]    = useState(false)  // modal conferência caixa
  const [conferData,    setConferData]    = useState(null)
  const [randomCode,    setRandomCode]    = useState('')
  const [userCode,      setUserCode]      = useState('')
  const [selectedId,    setSelectedId]    = useState(null)
  const [isSubmitting,  setIsSubmitting]  = useState(false)
  const [isClosing,     setIsClosing]     = useState(false)
  const [isPrinting,    setIsPrinting]    = useState(null)
  const [isLoadingConf, setIsLoadingConf] = useState(false)

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      const r = await apinode.post(`caixa/abrir-caixa/${localStorage.idconta}`, formData)
      toast.success(r.data.message || 'Caixa aberto com sucesso!')
      setFormData(p => ({ ...p, troco: '', data_abertura: '' }))
      if (showList) await fetchCaixas()
    } catch (err) { toast.error(err?.response?.data?.message || err.message) }
    finally { setIsSubmitting(false) }
  }

  const fetchCaixas = async () => {
    try {
      const r = await apinode.get(`caixa/listar-caixas/${localStorage.idconta}`)
      setCaixas(r.data)
    } catch (err) { toast.error(err.message) }
  }

  const openList = () => { fetchCaixas(); setShowList(true) }

  // Abre conferência de caixa
  const openConferencia = async (id_cx) => {
    try {
      setIsLoadingConf(true)
      const r = await apinode.get(`pedido/listardadoscaixa/${id_cx}/${localStorage.idconta}`)
      setConferData({ ...r.data, id_cx })
      setShowConfer(true)
    } catch { toast.error('Erro ao carregar dados do caixa.') }
    finally { setIsLoadingConf(false) }
  }

  const handleClose = (id) => {
    setRandomCode(Math.random().toString(36).substring(2, 8).toUpperCase())
    setSelectedId(id); setUserCode(''); setShowConf(true)
  }

  const handleConfirmClose = async () => {
    if (userCode.trim().toUpperCase() !== randomCode) { toast.error('Código incorreto.'); return }
    try {
      setIsClosing(true)
      await apinode.post(`pedido/fecharCaixa/${localStorage.idconta}`, { ID_CX: selectedId, USER: localStorage.idusuario })
      toast.success('Caixa fechado com sucesso!')
      setShowConf(false)
      await handlePrint(selectedId)
      await fetchCaixas()
    } catch (err) { toast.error(err?.response?.data?.message || 'Erro ao fechar.') }
    finally { setIsClosing(false) }
  }

  const handlePrint = async (id_cx) => {
    try {
      setIsPrinting(id_cx)
      const r = await apinode.get(`pedido/listardadoscaixa/${id_cx}/${localStorage.idconta}`)
      gerarPDF(id_cx, r.data.dadosCaixa, r.data.dataCaixa)
    } catch { toast.error('Erro ao gerar relatório.') }
    finally { setIsPrinting(null) }
  }

  const reopenPDF = () => {
    const s = localStorage.getItem('lastGeneratedPDF')
    if (s) pdfMake.createPdf(JSON.parse(s)).open()
    else toast.warn('Nenhum PDF em cache.')
  }

  // ── Conferência computada ────────────────────────────────────────────────
  const conferComp = (() => {
    if (!conferData?.dadosCaixa?.length) return null
    const sd = conferData.dadosCaixa
    const pedMap = {}
    sd.forEach(s => {
      if (!pedMap[s.idpedido]) {
        pedMap[s.idpedido] = {
          idpedido:      s.idpedido,
          cliente:       s.cliente,
          valorBruto:    parseFloat(s.valorpedido)      || 0,
          abatimento:    parseFloat(s.valor_devolvido)  || 0,
          saldoAbertoCR: parseFloat(s.saldo_aberto_cr)  || 0,
          pagamentos:    [],
        }
      }
      pedMap[s.idpedido].pagamentos.push({
        forma: s.forma_pgto, vlrRecebido: parseFloat(s.vlr_recebido) || 0,
        troco: parseFloat(s.troco) || 0, data: s.data,
      })
    })
    const pedidos = Object.values(pedMap)

    const fundo       = parseFloat(sd[0]?.fundo_caixa) || 0
    const totalBruto  = pedidos.reduce((t, p) => t + p.valorBruto, 0)
    const totalAbat   = pedidos.reduce((t, p) => t + p.abatimento, 0)
    const totalLiq    = totalBruto - totalAbat
    const totalRec    = sd.reduce((t, s) => t + (parseFloat(s.vlr_recebido) || 0), 0)
    const totalTroco  = sd.reduce((t, s) => t + (parseFloat(s.troco) || 0), 0)
    // Saldo real = soma das parcelas Em Aberto no CR (todos os caixas)
    const saldoPend   = pedidos.reduce((t, p) => t + p.saldoAbertoCR, 0)
    const caixaFisico = fundo + totalRec - totalTroco

    const pgtoMap = {}
    sd.forEach(s => {
      const k = s.forma_pgto || 'Outros'
      pgtoMap[k] = (pgtoMap[k] || 0) + (parseFloat(s.vlr_recebido) || 0)
    })

    const parciais = pedidos.filter(p => p.saldoAbertoCR > 0.009)

    return { pedidos, fundo, totalBruto, totalAbat, totalLiq, totalRec, totalTroco, saldoPend, caixaFisico, pgtoMap, parciais }
  })()

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <Navbar />
      <div className="g-root">
        <div className="g-wrap">

          {/* ── Hero ── */}
          <div className="g-hero">
            <div className="g-hero-text">
              <h1>Gestão de Caixa</h1>
              <p>Abertura, fechamento e conferência de caixas PDV</p>
            </div>
            <div className="g-hero-btns">
              <button className="g-btn g-btn-ghost" onClick={reopenPDF}>
                📄 Reimprimir PDF
              </button>
              <button className="g-btn g-btn-ghost" onClick={openList}>
                📋 Todos os Caixas
              </button>
            </div>
          </div>

          {/* ── Grid ── */}
          <div className="g-grid">
            {/* Abertura */}
            <div className="g-card">
              <div className="g-card-hd">
                <div className="g-card-hd-ico" style={{ background: '#dbeafe' }}>📂</div>
                <div>
                  <div className="g-card-hd-title">Abrir Novo Caixa</div>
                  <div className="g-card-hd-sub">Informe o fundo e o operador</div>
                </div>
              </div>
              <div className="g-card-bd">
                <form onSubmit={handleSubmit}>
                  <div className="g-field">
                    <label className="g-label">Fundo de Caixa (R$)</label>
                    <input type="number" name="troco" className="g-input" placeholder="0,00"
                      value={formData.troco} onChange={handleChange} step="0.01" min="0" required />
                  </div>
                  <div className="g-field">
                    <label className="g-label">Data e Hora de Abertura</label>
                    <input type="datetime-local" name="data_abertura" className="g-input"
                      value={formData.data_abertura} onChange={handleChange} required />
                  </div>
                  <div className="g-field" style={{ marginBottom: 22 }}>
                    <label className="g-label">Operador</label>
                    <input type="text" name="usuario_abertura" className="g-input"
                      value={formData.usuario_abertura} disabled />
                  </div>
                  <button type="submit" className="g-btn g-btn-primary g-btn-full" disabled={isSubmitting}>
                    {isSubmitting ? <><span className="g-spin" /> Abrindo...</> : '✓  Abrir Caixa'}
                  </button>
                </form>
              </div>
            </div>

            {/* Info cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="g-info" style={{ background: '#fef3c7', border: '1px solid #fcd34d' }}>
                <span className="g-info-ico">💡</span>
                <div>
                  <div className="g-info-title">Fundo de Caixa</div>
                  <div className="g-info-text">Informe o valor inicial em dinheiro disponível antes de iniciar as operações do dia.</div>
                </div>
              </div>
              <div className="g-info" style={{ background: '#fee2e2', border: '1px solid #fca5a5' }}>
                <span className="g-info-ico">🔒</span>
                <div>
                  <div className="g-info-title">Fechamento com Confirmação</div>
                  <div className="g-info-text">O fechamento exige código aleatório e gera relatório PDF com conferência completa automaticamente.</div>
                </div>
              </div>
              <div className="g-info" style={{ background: '#dbeafe', border: '1px solid #93c5fd' }}>
                <span className="g-info-ico">📊</span>
                <div>
                  <div className="g-info-title">Conferência de Parciais</div>
                  <div className="g-info-text">O relatório destaca pedidos com pagamento parcial, calculando saldo pendente por pedido e total geral.</div>
                </div>
              </div>
              <div className="g-info" style={{ background: '#ede9fe', border: '1px solid #c4b5fd' }}>
                <div>
                  <div className="g-info-title">Botão de Conferência</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Modal: Listagem de Caixas ── */}
      {showList && (
        <div className="g-overlay" onClick={e => e.target === e.currentTarget && setShowList(false)}>
          <div className="g-modal">
            <div className="g-modal-hd">
              <h4>📋 Caixas — Abertos e Fechados</h4>
              <button className="g-close" onClick={() => setShowList(false)}>✕</button>
            </div>
            <div className="g-modal-bd" style={{ padding: 0 }}>
              {caixas.length === 0
                ? <div className="g-empty"><span className="g-empty-ico">📭</span>Nenhum caixa encontrado.</div>
                : (
                  <div className="g-table-wrap">
                    <table className="g-table">
                      <thead>
                        <tr>
                          <th>ID</th><th>Abertura</th><th>Fechamento</th>
                          <th>Operador</th><th>Fundo</th><th>Status</th><th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {caixas.map(cx => {
                          const isOpen = !cx.data_fechamento
                          return (
                            <tr key={cx.id_cx} className={isOpen ? 'row-open' : 'row-closed'}>
                              <td>
                                <span className="g-mono" style={{ fontWeight: 700, color: 'var(--blue)', fontSize: '.85rem' }}>
                                  #{cx.id_cx}
                                </span>
                              </td>
                              <td style={{ fontSize: '.8rem' }}>{fmtDt(cx.data_abertura)}</td>
                              <td style={{ fontSize: '.8rem' }}>{cx.data_fechamento ? fmtDt(cx.data_fechamento) : '—'}</td>
                              <td style={{ fontWeight: 600 }}>{cx.nome_usuario_abertura || '—'}</td>
                              <td style={{ fontWeight: 700, fontFamily: 'var(--fm)', color: 'var(--green)', fontSize: '.85rem' }}>
                                {BRL(cx.fundo_caixa)}
                              </td>
                              <td>
                                {isOpen
                                  ? <span className="g-badge g-badge-green">● Aberto</span>
                                  : <span className="g-badge g-badge-gray">○ Fechado</span>}
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button className="g-icon-btn danger" data-tip="Fechar Caixa"
                                    disabled={!isOpen} onClick={() => handleClose(cx.id_cx)}>🔒</button>
                                  <button className="g-icon-btn" data-tip="Conferir Caixa"
                                    onClick={() => openConferencia(cx.id_cx)} disabled={isLoadingConf}>
                                  </button>
                                  <button className="g-icon-btn success" data-tip="Imprimir Relatório"
                                    onClick={() => handlePrint(cx.id_cx)} disabled={isPrinting === cx.id_cx}>
                                    {isPrinting === cx.id_cx ? <span className="g-spin g-spin-dark" style={{ borderColor: 'rgba(5,150,105,.2)', borderTopColor: 'var(--green)' }} /> : '🖨️'}
                                  </button>
                                  <button className="g-icon-btn" data-tip="Reimprimir último PDF" onClick={reopenPDF}>📄</button>
                                  <SangriaButton idCx={cx.id_cx} data_fx={cx.data_fechamento} />
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
            <div className="g-modal-ft">
              <button className="g-btn g-btn-outline g-btn-sm" onClick={() => setShowList(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Conferência de Caixa ── */}
      {showConfer && conferComp && (
        <div className="g-overlay" onClick={e => e.target === e.currentTarget && setShowConfer(false)}>
          <div className="g-modal">
            <div className="g-modal-hd">
              <div>
                <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginTop: 3 }}>
                  {fmtDt(conferData.dataCaixa)} · Operador: {conferData.dadosCaixa?.[0]?.nome_abertura || '—'}
                </div>
              </div>
              <button className="g-close" onClick={() => setShowConfer(false)}>✕</button>
            </div>
            <div className="g-modal-bd">

              {/* Stats rápidos */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
                {[
                  { label: 'Fundo', val: BRL(conferComp.fundo),      color: 'var(--blue)',   bg: 'var(--blue-lt)' },
                  { label: 'Recebido', val: BRL(conferComp.totalRec), color: 'var(--green)',  bg: 'var(--green-lt)' },
                  { label: 'Pendente', val: BRL(conferComp.saldoPend), color: conferComp.saldoPend > 0.009 ? 'var(--amber)' : 'var(--green)', bg: conferComp.saldoPend > 0.009 ? 'var(--amber-lt)' : 'var(--green-lt)' },
                  { label: 'Caixa Físico', val: BRL(conferComp.caixaFisico), color: 'var(--ink)', bg: 'var(--surface2)' },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '13px 16px' }}>
                    <div style={{ fontSize: '.67rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em', color: s.color, marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 900, color: s.color, fontFamily: 'var(--fm)' }}>{s.val}</div>
                  </div>
                ))}
              </div>

              {/* Pedidos parciais */}
              {conferComp.parciais.length > 0 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: '.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--amber)' }}>
                      ⚠ {conferComp.parciais.length} Pedido(s) com Pagamento Parcial
                    </span>
                  </div>
                  <div style={{ background: 'var(--amber-xt)', border: '1px solid var(--amber-lt)', borderRadius: 11, overflow: 'hidden', marginBottom: 20 }}>
                    <table className="g-table" style={{ fontSize: '.8rem' }}>
                      <thead>
                        <tr>
                          <th style={{ background: 'var(--amber-lt)' }}>Pedido</th>
                          <th style={{ background: 'var(--amber-lt)' }}>Cliente</th>
                          <th style={{ background: 'var(--amber-lt)', textAlign: 'right' }}>Líquido</th>
                          <th style={{ background: 'var(--amber-lt)', textAlign: 'right' }}>Recebido</th>
                          <th style={{ background: 'var(--amber-lt)', textAlign: 'right' }}>Saldo</th>
                          <th style={{ background: 'var(--amber-lt)' }}>Progresso</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conferComp.parciais.map(p => {
                          const liq    = p.valorBruto - p.abatimento
                          const saldo  = p.saldoAbertoCR
                          const rec    = getRecPedido(p)
                          const pct    = liq > 0 ? Math.round((rec / liq) * 100) : 0
                          return (
                            <tr key={p.idpedido}>
                              <td style={{ fontFamily: 'var(--fm)', fontWeight: 700, color: 'var(--amber)' }}>#{p.idpedido}</td>
                              <td style={{ fontWeight: 600 }}>{p.cliente}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'var(--fm)', color: 'var(--ink2)' }}>{BRL(liq)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'var(--fm)', color: 'var(--green)', fontWeight: 700 }}>{BRL(rec)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'var(--fm)', color: 'var(--red)', fontWeight: 700 }}>{BRL(saldo)}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div className="partial-bar-wrap">
                                    <div className="partial-bar" style={{ width: `${pct}%`, background: pct > 79 ? 'var(--green)' : pct > 49 ? 'var(--amber)' : 'var(--red)' }} />
                                  </div>
                                  <span style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--amber)', width: 34, flexShrink: 0 }}>{pct}%</span>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Todos os pedidos */}
              <div style={{ fontSize: '.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--muted)', marginBottom: 10 }}>
                Todos os Pedidos ({conferComp.pedidos.length})
              </div>
              <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11, overflow: 'hidden', marginBottom: 20 }}>
                <table className="g-table" style={{ fontSize: '.79rem' }}>
                  <thead>
                    <tr>
                      <th>Pedido</th><th>Cliente</th><th>Formas Pgto</th>
                      <th style={{ textAlign: 'right' }}>Bruto</th>
                      <th style={{ textAlign: 'right' }}>Líquido</th>
                      <th style={{ textAlign: 'right' }}>Recebido</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conferComp.pedidos.map(p => {
                      const liq   = p.valorBruto - p.abatimento
                      const saldo = p.saldoAbertoCR
                      const rec   = getRecPedido(p)
                      const isQ   = saldo < 0.009
                      const formas = [...new Set(p.pagamentos.map(pg => pg.forma))].join(', ')
                      return (
                        <tr key={p.idpedido}>
                          <td style={{ fontFamily: 'var(--fm)', fontWeight: 700, color: isQ ? 'var(--green)' : 'var(--amber)', fontSize: '.82rem' }}>
                            #{p.idpedido}
                          </td>
                          <td style={{ fontWeight: 600 }}>{p.cliente}</td>
                          <td>
                            {formas.split(', ').map(f => (
                              <span key={f} style={{ background: 'var(--blue-lt)', color: 'var(--blue-dk)', borderRadius: 6, padding: '2px 7px', fontSize: '.68rem', fontWeight: 700, marginRight: 4 }}>{f}</span>
                            ))}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--fm)', color: 'var(--muted)' }}>{BRL(p.valorBruto)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--fm)', color: 'var(--ink2)', fontWeight: 600 }}>{BRL(liq)}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--fm)', color: 'var(--green)', fontWeight: 700 }}>{BRL(rec)}</td>
                          <td>
                            {isQ
                              ? <span className="g-badge g-badge-green">✓ Quitado</span>
                              : <span className="g-badge g-badge-amber">⚠ {BRL(saldo)}</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Conferência financeira */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Por forma */}
                <div>
                  <div style={{ fontSize: '.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--muted)', marginBottom: 10 }}>Resumo por Forma</div>
                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {Object.entries(conferComp.pgtoMap).map(([forma, val], i) => (
                      <div key={forma} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '.82rem', fontWeight: 600, flex: 1 }}>{forma}</span>
                        <div className="partial-bar-wrap" style={{ maxWidth: 80 }}>
                          <div className="partial-bar" style={{ width: `${Math.round((val / conferComp.totalRec) * 100)}%`, background: PALETTE[i % PALETTE.length] }} />
                        </div>
                        <span style={{ fontFamily: 'var(--fm)', fontWeight: 700, fontSize: '.82rem', color: PALETTE[i % PALETTE.length], width: 80, textAlign: 'right' }}>{BRL(val)}</span>
                      </div>
                    ))}
                    <hr className="g-divider" style={{ margin: '4px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}>
                      <span style={{ fontSize: '.82rem' }}>Total Recebido</span>
                      <span style={{ fontFamily: 'var(--fm)', color: 'var(--green)' }}>{BRL(conferComp.totalRec)}</span>
                    </div>
                  </div>
                </div>

                {/* Conferência */}
                <div>
                  <div style={{ fontSize: '.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', color: 'var(--muted)', marginBottom: 10 }}>Conferência de Caixa</div>
                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11, overflow: 'hidden' }}>
                    {[
                      { label: 'Fundo de Caixa', val: BRL(conferComp.fundo), bg: '', vc: 'var(--ink)' },
                      { label: '(+) Vendas Brutas', val: BRL(conferComp.totalBruto), bg: '', vc: 'var(--ink)' },
                      ...(conferComp.totalAbat > 0.009 ? [{ label: '(−) Abatimentos', val: `−${BRL(conferComp.totalAbat)}`, bg: 'var(--red-xt)', vc: 'var(--red)' }] : []),
                      { label: '= Vendas Líquidas', val: BRL(conferComp.totalLiq), bg: 'var(--blue-xt)', vc: 'var(--blue-dk)', bold: true },
                      { label: '(+) Recebido', val: BRL(conferComp.totalRec), bg: '', vc: 'var(--green)' },
                      ...(conferComp.totalTroco > 0.009 ? [{ label: '(−) Troco', val: `−${BRL(conferComp.totalTroco)}`, bg: 'var(--red-xt)', vc: 'var(--red)' }] : []),
                      ...(conferComp.saldoPend > 0.009 ? [{ label: '⚠ Saldo Pendente', val: BRL(conferComp.saldoPend), bg: 'var(--amber-xt)', vc: 'var(--amber)', bold: true }] : []),
                      { label: 'TOTAL FÍSICO NO CAIXA', val: BRL(conferComp.caixaFisico), bg: 'var(--ink)', vc: '#fbbf24', bold: true },
                    ].map((row, i) => (
                      <div key={i} className="conf-row" style={{ background: row.bg || (i % 2 === 0 ? 'transparent' : 'var(--surface)'), borderRadius: 0 }}>
                        <span className="conf-label" style={{ fontWeight: row.bold ? 700 : 500, color: row.bg === 'var(--ink)' ? 'rgba(255,255,255,.7)' : 'var(--ink2)' }}>{row.label}</span>
                        <span className="conf-value" style={{ color: row.bg === 'var(--ink)' ? row.vc : row.vc }}>{row.val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="g-modal-ft">
              <button className="g-btn g-btn-outline g-btn-sm" onClick={() => setShowConfer(false)}>Fechar</button>
              <button className="g-btn g-btn-primary g-btn-sm" onClick={() => handlePrint(conferData.id_cx)} disabled={isPrinting === conferData.id_cx}>
                {isPrinting === conferData.id_cx ? <><span className="g-spin" />Gerando...</> : '🖨️ Imprimir Relatório'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Confirmação de Fechamento ── */}
      {showConf && (
        <div className="g-overlay" onClick={e => e.target === e.currentTarget && setShowConf(false)}>
          <div className="g-modal g-modal-sm">
            <div className="g-modal-hd">
              <h4>🔒 Fechar Caixa #{selectedId}</h4>
              <button className="g-close" onClick={() => setShowConf(false)}>✕</button>
            </div>
            <div className="g-modal-bd">
              <p style={{ color: 'var(--ink2)', fontSize: '.88rem', margin: '0 0 6px', lineHeight: 1.6 }}>
                Para confirmar o fechamento, reproduza exatamente o código abaixo. O relatório PDF será gerado automaticamente.
              </p>
              <div className="g-code">{randomCode}</div>
              <input
                className="g-input"
                placeholder="Digite o código acima..."
                value={userCode}
                onChange={e => setUserCode(e.target.value.toUpperCase())}
                style={{ fontFamily: 'var(--fm)', fontSize: '1.05rem', letterSpacing: '.15em', fontWeight: 700, textAlign: 'center', marginTop: 8 }}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleConfirmClose()}
              />
              <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginTop: 10, textAlign: 'center' }}>
                ⚠️ Após o fechamento esta ação não pode ser desfeita.
              </div>
            </div>
            <div className="g-modal-ft">
              <button className="g-btn g-btn-outline g-btn-sm" onClick={() => setShowConf(false)}>Cancelar</button>
              <button className="g-btn g-btn-danger g-btn-sm"
                onClick={handleConfirmClose}
                disabled={isClosing || userCode.trim().toUpperCase() !== randomCode}>
                {isClosing ? <><span className="g-spin" /> Fechando...</> : '🔒 Confirmar Fechamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AberturaCaixa